/**
 * Admin postal-return service.
 *
 * Reads & manipulates the postal-return wallet lock that's created when an
 * admin refunds a postal item via arbitration. The buyer's refund stays locked
 * until the seller confirms they received the returned product. If the seller
 * is unresponsive or rejects unfairly, the admin force-releases the lock here.
 */

import apiClient from '../utils/apiClient';

export type PostalReturnStatus =
  | 'pending_buyer_ship'
  | 'shipped_by_buyer'
  | 'received_by_seller'
  | 'rejected_by_seller'
  | 'timed_out';

export interface PostalReturnInfo {
  status: PostalReturnStatus;
  refundAmount: number;
  refundLockId?: string;
  buyerShipDeadline?: string;
  buyerTrackingCode?: string;
  buyerShippedAt?: string;
  buyerShipmentNotes?: string;
  sellerReceivedAt?: string;
  sellerReceiveNotes?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt?: string;
}

export interface AdminPostalReturnState {
  orderItemId: string;
  productTitle: string;
  storeName?: string;
  storeId?: string;
  isPostal: boolean;
  itemStatus: string;
  postalReturnInfo: PostalReturnInfo | null;
  canForceRelease: boolean;
}

const adminPostalReturnService = {
  async get(orderItemId: string): Promise<AdminPostalReturnState | null> {
    try {
      const res = await apiClient.get(`/admin/order-items/${orderItemId}/return`);
      return (res.data?.data || null) as AdminPostalReturnState | null;
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },

  async forceRelease(orderItemId: string, notes?: string): Promise<AdminPostalReturnState> {
    const res = await apiClient.post(
      `/admin/order-items/${orderItemId}/return/force-release`,
      { notes }
    );
    return res.data?.data;
  }
};

export default adminPostalReturnService;
